/**
 * Vorlage „Wenden“.
 *
 * Ein Pfannkuchen wird gewendet: entweder durch einen Schwung aus der Pfanne
 * oder mit dem Pfannenwender. Die Unterseite ist goldbraun mit dunklen
 * Stellen. Zwei Wendungen je Schleife, so liegt am Ende wieder dieselbe Seite
 * oben.
 */
import { clamp, ease, fract, segment } from '../geometry.ts';
import { compact, faded, group } from '../scene.ts';
import { motionLines } from '../parts/effects.ts';
import { pancake } from '../parts/food.ts';
import { arcArrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { place, turner } from '../parts/utensils.ts';
import { pan } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const PAN = { cx: 96, width: 124 } as const;
const RIM_Y = GRATE_Y - 13 - PAN.width * 0.1;
/** Oberseite des liegenden Pfannkuchens. */
const REST_Y = RIM_Y + 5;
const KNOB_X = 214;
const POWER = 0.6;

/** Vorlage „Wenden“. */
export const stoveFlipTemplate = defineTemplate({
  id: 'stove.flip',
  group: 'stove',
  title: 'Wenden',
  description:
    'Ist die Unterseite goldbraun, wird der Pfannkuchen gewendet – mit einem Schwung aus der Pfanne oder mit dem Pfannenwender.',
  durationSeconds: 4,
  keywords: ['wenden', 'umdrehen', 'pfannkuchen', 'crêpe', 'crepe', 'omelett', 'eierkuchen', 'goldbraun'],
  stillTime: 0.16,
  parameters: [choice('tool', 'Wenden mit', { toss: 'Schwung', turner: 'Pfannenwender' }, 'toss')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('wender')) return { tool: 'turner' };
    if (lower.includes('schwung') || lower.includes('schwenken')) return { tool: 'toss' };
    return {};
  },
  scene(values, t) {
    const tool = textValue(values, 'tool', 'toss');
    const local = fract(t * 2);
    const turn = Math.floor(t * 2);
    const flight = segment(local, 0.12, 0.55);
    const height = tool === 'toss' ? 46 : 30;
    const lift = 4 * flight * (1 - flight) * height;
    const angle = 180 * turn + 180 * ease.inOutSine(flight);
    // Beim Schwung ruckt die Pfanne kurz nach oben.
    const jerk = tool === 'toss' ? Math.sin(segment(local, 0.02, 0.16) * Math.PI) : 0;
    const vessel = pan(PAN.cx, RIM_Y, PAN.width);
    const burner = stove(PAN.cx, POWER, t);
    const cakeX = PAN.cx - 4 + Math.sin(flight * Math.PI) * 6;
    const cakeY = REST_Y - lift;
    const cake = pancake(cakeX, cakeY, 42, 15, angle, 'tint', 'shade');

    // Der Wender schiebt sich unter den Pfannkuchen, hebt ihn an und zieht sich zurück.
    const slide = ease.inOutCubic(segment(local, 0, 0.12));
    const retreat = ease.inOutCubic(segment(local, 0.3, 0.5));
    const peak = REST_Y - height;
    const turnerY = (flight < 0.5 ? cakeY : peak) + 6 + retreat * 30;
    const turnerX = cakeX - 46 + slide * 46 - retreat * 34;
    const turnerNode =
      tool === 'turner'
        ? faded(clamp(slide * 3) * (1 - retreat), [place(turnerX, turnerY, 0, turner())])
        : null;

    const panMotion = { y: -jerk * 6, rotate: -jerk * 3, ox: PAN.cx + 110, oy: RIM_Y };
    const airborne = lift > 0.5;
    return compact([
      ...counter(),
      ...burner.back,
      group(vessel.back, { transform: panMotion }),
      turnerNode,
      airborne ? null : cake,
      group(vessel.front, { transform: panMotion }),
      airborne ? cake : null,
      ...burner.front,
      motionLines(cakeX + 52, cakeY - 6, 250, 10, 3, 6, flight > 0.05 && flight < 0.6 ? 0.8 : 0),
      stoveKnob(KNOB_X, POWER),
    ]);
  },
  hints() {
    return [arcArrow(PAN.cx - 4, REST_Y - 14, 34, 24, 160, 380)];
  },
});
